import { useEventLogging } from '@AppModels/Logging';
import { useUserSession } from '@AppModels/hooks/appStateHooks';
import { useGetSystemJobQuery } from '@Queries/SystemJob/useGetSystemJobQuery';
import { bs } from '@Styles/styles';
import { THEME } from '@Theme';
import * as Api from '@ViewModels';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { DownloadIconV2 } from '@WebComponents/svgs/icons/DownloadIconV2';
import { WarningIcon } from '@WebComponents/svgs/icons/WarningIcon';
import { css, StyleSheet } from 'aphrodite';
import { saveAs } from 'file-saver';
import * as React from 'react';
import { toast } from 'sonner';
import { formatFileSize } from '../../../../utils/fileSize';
import { shadow } from '../../../styles/shadows';
import { Button } from '../../Button';
import { Attachment, AttachmentPreview, Attachments } from '../Attachments';

type MediaCategory = 'image' | 'video' | 'document';

// Image-generation attachments predate mimeType being populated but always carry a url,
// so a missing mimeType only means image when a url is present.
function getMediaCategory(attachment: Api.IFileAttachment): MediaCategory {
	if (attachment.mimeType?.startsWith('image/')) {
		return 'image';
	}
	if (attachment.mimeType?.startsWith('video/')) {
		return 'video';
	}
	if (!attachment.mimeType && attachment.url) {
		return 'image';
	}
	return 'document';
}

// Inline previews need a url; url-less media can only be offered as a download.
function isPreviewable(attachment: Api.IFileAttachment): boolean {
	return getMediaCategory(attachment) !== 'document' && Boolean(attachment.url);
}

interface MediaContentDisplayProps {
	toolCall: Api.MediaContentToolCall;
	impersonationContext?: Api.IImpersonationContext;
	onJobComplete?: () => void;
	onUseImage?: (fileAttachments: Api.IFileAttachment[]) => void;
}

export function MediaContentDisplay({
	toolCall,
	impersonationContext,
	onJobComplete,
	onUseImage,
}: MediaContentDisplayProps) {
	const notifiedJobIdRef = React.useRef<string | undefined>(undefined);

	const systemJobQuery = useGetSystemJobQuery<Api.ISystemJob & { fileAttachments?: Api.IFileAttachment[] }>({
		enabled: Boolean(toolCall.systemJobId),
		jobId: toolCall.systemJobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!toolCall.systemJobId) {
				return false;
			}
			return data?.percentComplete !== 100 ? 3000 : false;
		},
		refetchOnWindowFocus: false,
		staleTime: Infinity,
	});

	React.useEffect(() => {
		if (
			toolCall.systemJobId &&
			notifiedJobIdRef.current !== toolCall.systemJobId &&
			systemJobQuery.data?.percentComplete === 100 &&
			systemJobQuery.data?.status === 'Success'
		) {
			notifiedJobIdRef.current = toolCall.systemJobId;
			onJobComplete?.();
		}
	}, [systemJobQuery.data?.percentComplete, systemJobQuery.data?.status, onJobComplete, toolCall.systemJobId]);

	let fileAttachments: Api.IFileAttachment[] | undefined;
	if (toolCall.fileAttachments?.length) {
		fileAttachments = toolCall.fileAttachments;
	} else if (
		systemJobQuery.data?.percentComplete === 100 &&
		systemJobQuery.data?.status === 'Success' &&
		systemJobQuery.data.fileAttachments?.length
	) {
		fileAttachments = systemJobQuery.data.fileAttachments;
	}
	if (fileAttachments?.length) {
		const imageAttachments = fileAttachments.filter(
			attachment => getMediaCategory(attachment) === 'image' && attachment.url
		);
		const previewAttachments = fileAttachments.filter(isPreviewable);
		const downloadAttachments = fileAttachments.filter(attachment => !isPreviewable(attachment));
		return (
			<>
				{previewAttachments.length > 0 && (
					<div className={css(bs.flex, bs.gap3, bs.flexWrap, bs.mt3)}>
						{previewAttachments.map((attachment, idx) => (
							<MediaAttachment key={`generated-${toolCall.id}-${attachment.id ?? idx}`} attachment={attachment} />
						))}
					</div>
				)}
				{downloadAttachments.length > 0 && (
					<Attachments variant='list' styles={[bs.mt3]}>
						{downloadAttachments.map((attachment, idx) => (
							<MediaDocumentCard
								key={`generated-document-${toolCall.id}-${attachment.id ?? idx}`}
								attachment={attachment}
								impersonationContext={impersonationContext}
							/>
						))}
					</Attachments>
				)}
				{onUseImage && imageAttachments.length > 0 && (
					<div className={css(bs.flex, bs.justifyCenter, bs.mt3, bs.mb3)}>
						<Button variant='primary' onClick={() => onUseImage(imageAttachments)}>
							Use This Image
						</Button>
					</div>
				)}
			</>
		);
	}

	if (systemJobQuery.data?.percentComplete === 100 && systemJobQuery.data?.status !== 'Success') {
		return (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.p3, bs.mt2, styles.errorMessage)}>
				<WarningIcon />
				<span>Failed to generate media</span>
			</div>
		);
	}

	return (
		<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.p3, bs.mt2, styles.processingMessage)}>
			<LoadingSpinner type='tiny' />
			<span>Generating media...</span>
		</div>
	);
}

function MediaAttachment({ attachment }: { attachment: Api.IFileAttachment }) {
	if (getMediaCategory(attachment) === 'video') {
		return (
			<video
				src={attachment.url}
				controls
				preload='metadata'
				aria-label={attachment.fileName || 'Generated video'}
				className={css(styles.mediaPreview)}
			/>
		);
	}
	return (
		<img src={attachment.url} alt={attachment.fileName || 'Generated image'} className={css(styles.mediaPreview)} />
	);
}

function MediaDocumentCard({
	attachment,
	impersonationContext,
}: {
	attachment: Api.IFileAttachment;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const logger = useEventLogging('MediaContentDisplay');
	const userSession = useUserSession();
	const [isDownloading, setIsDownloading] = React.useState(false);

	const handleDownload = async () => {
		const fileName = attachment.fileName || 'download';
		if (attachment.url) {
			saveAs(attachment.url, fileName);
			return;
		}
		// Generated documents live in private storage; a short-lived signed url is resolved on demand.
		setIsDownloading(true);
		try {
			const fresh = await userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `attachment/${attachment.id}` }),
				'GET'
			);
			if (!fresh?.url) {
				throw new Error('Attachment has no download url');
			}
			saveAs(fresh.url, fresh.fileName || fileName);
		} catch (error) {
			logger.logApiError('MediaContentDownload-Error', error as Api.IOperationResultNoValue);
			toast.error('Failed to download file');
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<Attachment
			data={{
				id: attachment.id,
				type: 'file',
				filename: attachment.fileName,
				mediaType: attachment.mimeType ?? '',
				url: attachment.url ?? '',
			}}
		>
			<AttachmentPreview />
			<div className={css(styles.documentInfo)}>
				<span className={css(styles.documentName)}>{attachment.fileName || 'Generated document'}</span>
				{attachment.fileSize > 0 && (
					<span className={css(styles.documentMeta)}>{formatFileSize(attachment.fileSize)}</span>
				)}
			</div>
			<Button
				variant='ghost'
				size='icon'
				isLoading={isDownloading}
				aria-label={`Download ${attachment.fileName || 'document'}`}
				title={`Download ${attachment.fileName || 'document'}`}
				onClick={handleDownload}
			>
				<DownloadIconV2 />
			</Button>
		</Attachment>
	);
}

const styles = StyleSheet.create({
	documentInfo: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		minWidth: 0,
	},
	documentMeta: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	documentName: {
		...THEME.common.truncate,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	errorMessage: {
		backgroundColor: THEME.colors.danger.light,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.danger.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	mediaPreview: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadow,
		maxHeight: 400,
		maxWidth: 400,
		objectFit: 'contain',
	},
	processingMessage: {
		backgroundColor: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
});
